//loadSkillsets, bulkUpdateSkillsetsCallBack
import { useState } from 'react'

import {
  useLazyListSkillsetsQuery,
  useGetOneSkillsetQuery, // this one is the for initial load, which happens before the ui renders anything
  useUpsertSkillsetMutation,
  useBulkUpdateSkillsetsMutation,
  useDeleteSkillsetMutation
} from '../../apiSlice/skillsetApi'

import { useUpdateSkillsetForPartiesMutation } from '../../apiSlice/partyApi'

import useSearchCallback from '../../apiSlice/useSearchCallback'
import useFetchAuxiliaryLists from '../../apiSlice/useFetchAuxiliaryLists'
import { BucketConfig } from '../../../sharedcomponents/5templates/detailCards/BucketConfig'

import {
  ArtificialDetailPropertyNames,
  RootObjectTypes
} from '../../../common/globalNotary'
import { getPartySkillsetAssociations } from '../../apiSlice/pushPullHelper'
import { useNavigate } from 'react-router-dom'

const useSkillset = () => {
  const [loadSkillsets] = useLazyListSkillsetsQuery()
  const [bulkUpdateSkillsetsCallBack] = useBulkUpdateSkillsetsMutation()
  const [upsertSkillsetRecordCallback] = useUpsertSkillsetMutation()
  const [updateSkillsetForParties] = useUpdateSkillsetForPartiesMutation()
  const [deleteSkillset] = useDeleteSkillsetMutation()
  const navigate = useNavigate()

  const { searchPartiesCallback } = useSearchCallback()

  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState(null)
  const [loading, setLoading] = useState(false)

  const constructAndSetFinalListBucket = (
    completeResponse,
    primarySkillsetRecord
  ) => {
    const finalListBuckets = {}
    finalListBuckets.primero = []
    finalListBuckets.segundo = [
      new BucketConfig(
        completeResponse[0].data,
        searchPartiesCallback,
        'Skillset Owner',
        null,
        'genericSelect',
        'decorated',
        {
          titleArray: ['Completed Date', 'Expired Date', 'Document Link'],
          controlArray: [
            {
              type: 'date',
              baseName: `${ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1}[INDEX].effective_at`,
              disabled: false
            },
            {
              type: 'date',
              baseName: `${ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1}[INDEX].expires_at`,
              disabled: false
            },
            {
              type: 'url',
              baseName: `${ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1}[INDEX].doc_url`,
              disabled: false
            }
          ]
        }
      )
    ]

    setFinalAuxiliaryRecords(finalListBuckets)
  }

  const { constructValueAsPromiseCall } = useFetchAuxiliaryLists()

  const fetchAuxiliaryRecords = async primarySkillsetRecord => {
    const equalityEvaluator = (wideItem, narrowItem) => {
      return (
        wideItem.skillset_id === narrowItem.skillset_id &&
        wideItem.party_id === narrowItem.party_id
      )
    }

    const fetchCallArray = [
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1,
        primarySkillsetRecord[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1
        ],
        ['party_id', 'skillset_id', 'effective_at', 'expires_at', 'doc_url'],
        equalityEvaluator
      )
    ]

    /// this Promise.all will not reject, errors come back on the error object,
    /// if everything works out, the error object is null or undefined
    Promise.all(fetchCallArray.map(f => f.call())).then(res => {
      constructAndSetFinalListBucket(res, primarySkillsetRecord)
    })
  }

  const makeInMemorySkillsetRecord = type => {
    return {
      name: '',
      description: '',
      active: true,
      _objectId: '',
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES]: []
    }
  }

  const removeSkillset = id => {
    deleteSkillset({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete skillset. Please check that other objects do not depend on it.')
    })
  }

  const suspendSkillset = async (id, active) => {
    bulkUpdateSkillsetsCallBack([
      { id, suspended_at: active ? null : new Date() }
    ])
  }

  const upsertSkillsetCallback = primarySkillsetRecord => {
    const returnPromise = new Promise((resolve, reject) => {
      setLoading(true)
      upsertSkillsetRecordCallback(primarySkillsetRecord)
        .unwrap()
        .then(primaryResult => {
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id

          const currentPartiesList =
            primarySkillsetRecord[
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1
            ]

          for (let i = 0; i < currentPartiesList.length; i++) {
            currentPartiesList[i].skillset_id = id
            currentPartiesList[i].effective_at = currentPartiesList[i].effective_at || null
            currentPartiesList[i].expires_at = currentPartiesList[i].expires_at || null
            currentPartiesList[i].doc_url = currentPartiesList[i].doc_url || null
          }

          const partyAndSkillset = getPartySkillsetAssociations(
            primarySkillsetRecord[
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1
            ],
            primarySkillsetRecord[
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
            ]
          )

          updateSkillsetForParties(partyAndSkillset)
            .unwrap()
            .then(res => {
              const errors = res.data?.errors ?? []
              errors.length === 0
                ? resolve({ id, type: RootObjectTypes.SKILLSET })
                : reject(
                  'The following errors have occurred: ' +
                  errors.map(e => e.msg).join(", ")
                )
            })
            .catch(e => {
              reject(e)
            })
        })
        .catch(e => reject(e))
        .finally(() => {
          setLoading(false)
        })
    })

    return returnPromise
  }

  return {
    loadSkillsets,
    bulkUpdateSkillsetsCallBack,
    useGetOneSkillsetQuery,
    fetchAuxiliaryRecords,
    makeInMemorySkillsetRecord,
    finalAuxiliaryRecords,
    upsertSkillsetCallback,
    removeSkillset,
    suspendSkillset,
    loading
  }
}

export default useSkillset
